import { csiAPI } from "src/boot/axios"
import { CSIurl } from "src/util/enums/urls"
import { concatUrl, getCustomConfig, getNewAbortHandler } from "src/util/http/httpHelper"
import {
  AttributeChar,
  AttributeFilterParams,
  AttributeNumeric,
  AttributeSelect,
  AttributeSelectOptions,
  AttributeTemporal,
  AttributeValueCharFull,
  AttributeValueNumericFull,
  AttributeValueSelectFull,
  AttributeValueTemporalFull,
} from "src/util/types/dataModels/attribute"
import { ObjectList, PayloadEntity } from "src/util/types/dataModels/genericObject"
import { HttpConfig } from "src/util/types/dataModels/http"
import {
  PERMISSION_ACCESS_CREATE,
  PERMISSION_ACCESS_READ,
  PERMISSION_ACCESS_UPDATE,
  PERMISSION_ATTRIBUTES,
} from "src/util/types/dataModels/user"

//region Attribute Char

export async function getAttributeCharList(
  config: HttpConfig = {}
): Promise<ObjectList<AttributeChar>> {
  const entityURL = CSIurl.ATTRIBUTES_CHAR_LIST
  const url = concatUrl(entityURL, { ...config?.mainParams })
  const abortHandler = config?.abortHandler || getNewAbortHandler("GET", entityURL)
  const customConfig = getCustomConfig(abortHandler, PERMISSION_ATTRIBUTES, PERMISSION_ACCESS_READ)
  const signal = abortHandler.controller.signal

  const { data } = await csiAPI.get(url, { signal, customConfig })

  return data
}

export async function postAttributeChar(
  config: HttpConfig = {},
  attribute: PayloadEntity<AttributeChar>
): Promise<AttributeChar> {
  const url = CSIurl.ATTRIBUTES_CHAR_LIST
  const abortHandler = config?.abortHandler || getNewAbortHandler("POST", url)
  const customConfig = getCustomConfig(
    abortHandler,
    PERMISSION_ATTRIBUTES,
    PERMISSION_ACCESS_CREATE
  )
  const signal = abortHandler.controller.signal

  const { data } = await csiAPI.post(url, attribute, { signal, customConfig })

  return data
}

export async function getAttributeChar(
  config: HttpConfig = {},
  attributeId: string
): Promise<AttributeChar> {
  const url = CSIurl.ATTRIBUTES_CHAR.replace("{id}", attributeId)
  const abortHandler = config?.abortHandler || getNewAbortHandler("GET", url)
  const customConfig = getCustomConfig(abortHandler, PERMISSION_ATTRIBUTES, PERMISSION_ACCESS_READ)
  const signal = abortHandler.controller.signal

  const { data } = await csiAPI.get(url, { signal, customConfig })

  return data
}

export async function putAttributeChar(
  config: HttpConfig = {},
  attributeId: string,
  attribute: PayloadEntity<AttributeChar>
): Promise<AttributeChar> {
  const url = CSIurl.ATTRIBUTES_CHAR.replace("{id}", attributeId)
  const abortHandler = config?.abortHandler || getNewAbortHandler("PUT", url)
  const customConfig = getCustomConfig(
    abortHandler,
    PERMISSION_ATTRIBUTES,
    PERMISSION_ACCESS_UPDATE
  )
  const signal = abortHandler.controller.signal

  const { data } = await csiAPI.put(url, attribute, { signal, customConfig })

  return data
}

export async function patchAttributeChar(
  config: HttpConfig = {},
  attributeId: string,
  attribute: Partial<PayloadEntity<AttributeChar>>
): Promise<AttributeChar> {
  const url = CSIurl.ATTRIBUTES_CHAR.replace("{id}", attributeId)
  const abortHandler = config?.abortHandler || getNewAbortHandler("PATCH", url)
  const customConfig = getCustomConfig(
    abortHandler,
    PERMISSION_ATTRIBUTES,
    PERMISSION_ACCESS_UPDATE
  )
  const signal = abortHandler.controller.signal

  const { data } = await csiAPI.patch(url, attribute, { signal, customConfig })

  return data
}

export async function patchAttributeCharValuesBatch(
  config: HttpConfig = {},
  attributes: Partial<AttributeValueCharFull>[]
): Promise<ObjectList<AttributeValueCharFull>> {
  const url = CSIurl.ATTRIBUTES_CHAR_VALUES_BATCH
  const abortHandler = config?.abortHandler || getNewAbortHandler("PATCH", url)
  const customConfig = getCustomConfig(
    abortHandler,
    PERMISSION_ATTRIBUTES,
    PERMISSION_ACCESS_UPDATE
  )
  const signal = abortHandler.controller.signal

  const { data } = await csiAPI.patch(url, attributes, { signal, customConfig })

  return data
}

//endregion

//region Attribute Numeric

export async function getAttributeNumericList(
  config: HttpConfig = {}
): Promise<ObjectList<AttributeNumeric>> {
  const entityURL = CSIurl.ATTRIBUTES_NUMERIC_LIST
  const url = concatUrl(entityURL, { ...config?.mainParams })
  const abortHandler = config?.abortHandler || getNewAbortHandler("GET", entityURL)
  const customConfig = getCustomConfig(abortHandler, PERMISSION_ATTRIBUTES, PERMISSION_ACCESS_READ)
  const signal = abortHandler.controller.signal

  const { data } = await csiAPI.get(url, { signal, customConfig })

  return data
}

export async function postAttributeNumeric(
  config: HttpConfig = {},
  attribute: PayloadEntity<AttributeNumeric>
): Promise<AttributeNumeric> {
  const url = CSIurl.ATTRIBUTES_NUMERIC_LIST
  const abortHandler = config?.abortHandler || getNewAbortHandler("POST", url)
  const customConfig = getCustomConfig(
    abortHandler,
    PERMISSION_ATTRIBUTES,
    PERMISSION_ACCESS_CREATE
  )
  const signal = abortHandler.controller.signal

  const { data } = await csiAPI.post(url, attribute, { signal, customConfig })

  return data
}

export async function getAttributeNumeric(
  config: HttpConfig = {},
  attributeId: string
): Promise<AttributeNumeric> {
  const url = CSIurl.ATTRIBUTES_NUMERIC.replace("{id}", attributeId)
  const abortHandler = config?.abortHandler || getNewAbortHandler("GET", url)
  const customConfig = getCustomConfig(abortHandler, PERMISSION_ATTRIBUTES, PERMISSION_ACCESS_READ)
  const signal = abortHandler.controller.signal

  const { data } = await csiAPI.get(url, { signal, customConfig })

  return data
}

export async function putAttributeNumeric(
  config: HttpConfig = {},
  attributeId: string,
  attribute: PayloadEntity<AttributeNumeric>
): Promise<AttributeNumeric> {
  const url = CSIurl.ATTRIBUTES_NUMERIC.replace("{id}", attributeId)
  const abortHandler = config?.abortHandler || getNewAbortHandler("PUT", url)
  const customConfig = getCustomConfig(
    abortHandler,
    PERMISSION_ATTRIBUTES,
    PERMISSION_ACCESS_UPDATE
  )
  const signal = abortHandler.controller.signal

  const { data } = await csiAPI.put(url, attribute, { signal, customConfig })

  return data
}

export async function patchAttributeNumeric(
  config: HttpConfig = {},
  attributeId: string,
  attribute: Partial<PayloadEntity<AttributeNumeric>>
): Promise<AttributeNumeric> {
  const url = CSIurl.ATTRIBUTES_NUMERIC.replace("{id}", attributeId)
  const abortHandler = config?.abortHandler || getNewAbortHandler("PATCH", url)
  const customConfig = getCustomConfig(
    abortHandler,
    PERMISSION_ATTRIBUTES,
    PERMISSION_ACCESS_UPDATE
  )
  const signal = abortHandler.controller.signal

  const { data } = await csiAPI.patch(url, attribute, { signal, customConfig })

  return data
}

export async function patchAttributeNumericValuesBatch(
  config: HttpConfig = {},
  attributes: Partial<AttributeValueNumericFull>[]
): Promise<ObjectList<AttributeValueNumericFull>> {
  const url = CSIurl.ATTRIBUTES_NUMERIC_VALUES_BATCH
  const abortHandler = config?.abortHandler || getNewAbortHandler("PATCH", url)
  const customConfig = getCustomConfig(
    abortHandler,
    PERMISSION_ATTRIBUTES,
    PERMISSION_ACCESS_UPDATE
  )
  const signal = abortHandler.controller.signal

  const { data } = await csiAPI.patch(url, attributes, { signal, customConfig })

  return data
}

//endregion

//region Attribute Select

export async function getAttributeSelectList(
  config: HttpConfig = {}
): Promise<ObjectList<AttributeSelect>> {
  const entityURL = CSIurl.ATTRIBUTES_SELECT_LIST
  const url = concatUrl(entityURL, { ...config?.mainParams })
  const abortHandler = config?.abortHandler || getNewAbortHandler("GET", entityURL)
  const customConfig = getCustomConfig(abortHandler, PERMISSION_ATTRIBUTES, PERMISSION_ACCESS_READ)
  const signal = abortHandler.controller.signal

  const { data } = await csiAPI.get(url, { signal, customConfig })

  return data
}

export async function postAttributeSelect(
  config: HttpConfig = {},
  attribute: PayloadEntity<AttributeSelect>
): Promise<AttributeSelect> {
  const url = CSIurl.ATTRIBUTES_SELECT_LIST
  const abortHandler = config?.abortHandler || getNewAbortHandler("POST", url)
  const customConfig = getCustomConfig(
    abortHandler,
    PERMISSION_ATTRIBUTES,
    PERMISSION_ACCESS_CREATE
  )
  const signal = abortHandler.controller.signal

  const { data } = await csiAPI.post(url, attribute, { signal, customConfig })

  return data
}

export async function getAttributeSelect(
  config: HttpConfig = {},
  attributeId: string
): Promise<AttributeSelect> {
  const url = CSIurl.ATTRIBUTES_SELECT.replace("{id}", attributeId)
  const abortHandler = config?.abortHandler || getNewAbortHandler("GET", url)
  const customConfig = getCustomConfig(abortHandler, PERMISSION_ATTRIBUTES, PERMISSION_ACCESS_READ)
  const signal = abortHandler.controller.signal

  const { data } = await csiAPI.get(url, { signal, customConfig })

  return data
}

export async function putAttributeSelect(
  config: HttpConfig = {},
  attributeId: string,
  attribute: PayloadEntity<AttributeSelect>
): Promise<AttributeSelect> {
  const url = CSIurl.ATTRIBUTES_SELECT.replace("{id}", attributeId)
  const abortHandler = config?.abortHandler || getNewAbortHandler("PUT", url)
  const customConfig = getCustomConfig(
    abortHandler,
    PERMISSION_ATTRIBUTES,
    PERMISSION_ACCESS_UPDATE
  )
  const signal = abortHandler.controller.signal

  const { data } = await csiAPI.put(url, attribute, { signal, customConfig })

  return data
}

export async function patchAttributeSelect(
  config: HttpConfig = {},
  attributeId: string,
  attribute: Partial<PayloadEntity<AttributeSelect>>
): Promise<AttributeSelect> {
  const url = CSIurl.ATTRIBUTES_SELECT.replace("{id}", attributeId)
  const abortHandler = config?.abortHandler || getNewAbortHandler("PATCH", url)
  const customConfig = getCustomConfig(
    abortHandler,
    PERMISSION_ATTRIBUTES,
    PERMISSION_ACCESS_UPDATE
  )
  const signal = abortHandler.controller.signal

  const { data } = await csiAPI.patch(url, attribute, { signal, customConfig })

  return data
}

export async function patchAttributeSelectValuesBatch(
  config: HttpConfig = {},
  attributes: Partial<AttributeValueSelectFull>[]
): Promise<ObjectList<AttributeValueSelectFull>> {
  const url = CSIurl.ATTRIBUTES_SELECT_VALUES_BATCH
  const abortHandler = config?.abortHandler || getNewAbortHandler("PATCH", url)
  const customConfig = getCustomConfig(
    abortHandler,
    PERMISSION_ATTRIBUTES,
    PERMISSION_ACCESS_UPDATE
  )
  const signal = abortHandler.controller.signal

  const { data } = await csiAPI.patch(url, attributes, { signal, customConfig })

  return data
}

//endregion

//region Attribute Select Options

/**
 * Returns the list of select options based on the attribute id
 *
 * @param config custom configurations and filtering params
 * @param attributeId
 */
export async function getAttributeSelectOptionsList(
  config: HttpConfig = {},
  attributeId: string
): Promise<ObjectList<AttributeSelectOptions>> {
  const entityURL = CSIurl.ATTRIBUTES_SELECT_OPTIONS_ATTR_ID.replace("{attr_id}", attributeId)
  const url = concatUrl(entityURL, { ...config?.mainParams })
  const abortHandler = config?.abortHandler || getNewAbortHandler("GET", entityURL)
  const customConfig = getCustomConfig(abortHandler, PERMISSION_ATTRIBUTES, PERMISSION_ACCESS_READ)
  const signal = abortHandler.controller.signal

  const { data } = await csiAPI.get(url, { signal, customConfig })

  return data
}

/**
 * Returns a list of attributes' select options
 *
 * @param config custom configurations and filtering params
 */
export async function getAttributesSelectOptionsList(
  config: HttpConfig<AttributeFilterParams> = {}
): Promise<ObjectList<AttributeSelectOptions>> {
  const entityURL = CSIurl.ATTRIBUTES_SELECT_OPTIONS_LIST
  const url = concatUrl(entityURL, { ...config?.mainParams, ...config?.filterParams })
  const abortHandler = config?.abortHandler || getNewAbortHandler("GET", entityURL)
  const customConfig = getCustomConfig(abortHandler, PERMISSION_ATTRIBUTES, PERMISSION_ACCESS_READ)
  const signal = abortHandler.controller.signal

  const { data } = await csiAPI.get(url, { signal, customConfig })

  return data
}

/**
 * Creates a list of attributes' select options
 *
 * @param config custom configurations and filtering params
 * @param selectOption
 */
export async function postAttributesSelectOptions(
  config: HttpConfig = {},
  selectOption: PayloadEntity<AttributeSelectOptions>
): Promise<AttributeSelectOptions> {
  const url = CSIurl.ATTRIBUTES_SELECT_OPTIONS_LIST
  const abortHandler = config?.abortHandler || getNewAbortHandler("POST", url)
  const customConfig = getCustomConfig(
    abortHandler,
    PERMISSION_ATTRIBUTES,
    PERMISSION_ACCESS_CREATE
  )
  const signal = abortHandler.controller.signal

  const { data } = await csiAPI.post(url, selectOption, { signal, customConfig })

  return data
}

/**
 * Returns a list of attribute select options based on the attribute option id
 *
 * @param config custom configurations and filtering params
 * @param attributeOptionId the attribute option id of the list
 */
export async function getAttributesSelectOption(
  config: HttpConfig = {},
  attributeOptionId: string
): Promise<AttributeSelectOptions> {
  const url = CSIurl.ATTRIBUTES_SELECT_OPTIONS.replace("{id}", attributeOptionId)
  const abortHandler = config?.abortHandler || getNewAbortHandler("GET", url)
  const customConfig = getCustomConfig(abortHandler, PERMISSION_ATTRIBUTES, PERMISSION_ACCESS_READ)
  const signal = abortHandler.controller.signal

  const { data } = await csiAPI.get(url, { signal, customConfig })

  return data
}

/**
 * Updates an attribute select options list based on the option id
 *
 * @param config custom configurations and filtering params
 * @param attributeOptionId the attribute option id of the list, which is to be updated
 * @param selectOption the updated list
 */
export async function putAttributesSelectOption(
  config: HttpConfig = {},
  attributeOptionId: string,
  selectOption: PayloadEntity<AttributeSelectOptions>
): Promise<AttributeSelectOptions> {
  const url = CSIurl.ATTRIBUTES_SELECT_OPTIONS.replace("{id}", attributeOptionId)
  const abortHandler = config?.abortHandler || getNewAbortHandler("PUT", url)
  const customConfig = getCustomConfig(
    abortHandler,
    PERMISSION_ATTRIBUTES,
    PERMISSION_ACCESS_UPDATE
  )
  const signal = abortHandler.controller.signal

  const { data } = await csiAPI.put(url, selectOption, { signal, customConfig })

  return data
}

/**
 * Partially updates an attribute select options list based on the option id
 *
 * @param config custom configurations and filtering params
 * @param attributeOptionId the attribute option id of the list, which is to be updated
 * @param selectOption the updated list
 */
export async function patchAttributesSelectOption(
  config: HttpConfig = {},
  attributeOptionId: string,
  selectOption: Partial<PayloadEntity<AttributeSelectOptions>>
): Promise<AttributeSelectOptions> {
  const url = CSIurl.ATTRIBUTES_SELECT_OPTIONS.replace("{id}", attributeOptionId)
  const abortHandler = config?.abortHandler || getNewAbortHandler("PATCH", url)
  const customConfig = getCustomConfig(
    abortHandler,
    PERMISSION_ATTRIBUTES,
    PERMISSION_ACCESS_UPDATE
  )
  const signal = abortHandler.controller.signal

  const { data } = await csiAPI.patch(url, selectOption, { signal, customConfig })

  return data
}

//endregion

//region Attribute Temporal

export async function getAttributeTemporalList(
  config: HttpConfig = {}
): Promise<ObjectList<AttributeTemporal>> {
  const entityURL = CSIurl.ATTRIBUTES_TEMPORAL_LIST
  const url = concatUrl(entityURL, { ...config?.mainParams })
  const abortHandler = config?.abortHandler || getNewAbortHandler("GET", entityURL)
  const customConfig = getCustomConfig(abortHandler, PERMISSION_ATTRIBUTES, PERMISSION_ACCESS_READ)
  const signal = abortHandler.controller.signal

  const { data } = await csiAPI.get(url, { signal, customConfig })

  return data
}

export async function postAttributeTemporal(
  config: HttpConfig = {},
  attribute: PayloadEntity<AttributeTemporal>
): Promise<AttributeTemporal> {
  const url = CSIurl.ATTRIBUTES_TEMPORAL_LIST
  const abortHandler = config?.abortHandler || getNewAbortHandler("POST", url)
  const customConfig = getCustomConfig(
    abortHandler,
    PERMISSION_ATTRIBUTES,
    PERMISSION_ACCESS_CREATE
  )
  const signal = abortHandler.controller.signal

  const { data } = await csiAPI.post(url, attribute, { signal, customConfig })

  return data
}

export async function getAttributeTemporal(
  config: HttpConfig = {},
  attributeId: string
): Promise<AttributeTemporal> {
  const url = CSIurl.ATTRIBUTES_TEMPORAL.replace("{id}", attributeId)
  const abortHandler = config?.abortHandler || getNewAbortHandler("GET", url)
  const customConfig = getCustomConfig(abortHandler, PERMISSION_ATTRIBUTES, PERMISSION_ACCESS_READ)
  const signal = abortHandler.controller.signal

  const { data } = await csiAPI.get(url, { signal, customConfig })

  return data
}

export async function putAttributeTemporal(
  config: HttpConfig = {},
  attributeId: string,
  attribute: PayloadEntity<AttributeTemporal>
): Promise<AttributeTemporal> {
  const url = CSIurl.ATTRIBUTES_TEMPORAL.replace("{id}", attributeId)
  const abortHandler = config?.abortHandler || getNewAbortHandler("PUT", url)
  const customConfig = getCustomConfig(
    abortHandler,
    PERMISSION_ATTRIBUTES,
    PERMISSION_ACCESS_UPDATE
  )
  const signal = abortHandler.controller.signal

  const { data } = await csiAPI.put(url, attribute, { signal, customConfig })

  return data
}

export async function patchAttributeTemporal(
  config: HttpConfig = {},
  attributeId: string,
  attribute: Partial<PayloadEntity<AttributeTemporal>>
): Promise<AttributeTemporal> {
  const url = CSIurl.ATTRIBUTES_TEMPORAL.replace("{id}", attributeId)
  const abortHandler = config?.abortHandler || getNewAbortHandler("POST", url)
  const customConfig = getCustomConfig(
    abortHandler,
    PERMISSION_ATTRIBUTES,
    PERMISSION_ACCESS_UPDATE
  )
  const signal = abortHandler.controller.signal

  const { data } = await csiAPI.patch(url, attribute, { signal, customConfig })

  return data
}

export async function patchAttributeTemporalValuesBatch(
  config: HttpConfig = {},
  attributes: Partial<AttributeValueTemporalFull>[]
): Promise<ObjectList<AttributeValueTemporalFull>> {
  const url = CSIurl.ATTRIBUTES_TEMPORAL_VALUES_BATCH
  const abortHandler = config?.abortHandler || getNewAbortHandler("PATCH", url)
  const customConfig = getCustomConfig(
    abortHandler,
    PERMISSION_ATTRIBUTES,
    PERMISSION_ACCESS_UPDATE
  )
  const signal = abortHandler.controller.signal

  const { data } = await csiAPI.patch(url, attributes, { signal, customConfig })

  return data
}

//endregion
